import React, { useContext, useMemo, useCallback } from 'react';
import _, { map } from 'underscore';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { FontIcon, ChoiceGroup } from '@fluentui/react';
import { ConversionDataContext } from '@bingads-webui-campaign-react/conversion-data';
import { IS_EMAIL_SUPPORTED_ENABLED } from '@bingads-webui-campaign-react/conversion-utils';
import { optionSelectorStyle } from '../styles/edit-website-view-styles';

export const OptionsChoiceGroup = (props) => {
  const {
    isGTMEnabled,
    isEmailSupported,
    onChangeOptions,
    selectedOption,
  } = props;

  const i18n = useContext(I18nContext);
  const { instrumentation } = useContext(ConversionDataContext);

  const options = useMemo(() => _.pick({
    THIRD_PARTY_TAG: 'thirdPartyTag',
    GOOGLE_TAG_MANAGER: isGTMEnabled ? 'googleTagManager' : null,
    COPY_TAG: 'copyTag',
    EMAIL_TAG: isEmailSupported ? 'emailTag' : null,
  }, id => !_.isNull(id)), [isEmailSupported, isGTMEnabled]);

  const optionStrings = useMemo(() => ({
    thirdPartyTag: {
      title: _TL_('Let me add the tracking tag through a website editing platform'),
      text: _TL_('We\'ll provide instructions for adding the tracking tag through an editing platform, such as WordPress, Wix, Shopify, or BigCommerce.'),
      iconName: 'SingleColumnEdit',
    },
    copyTag: {
      title: _TL_('Let me edit my website\'s code myself'),
      text: _TL_('We\'ll provide instructions so you can add the tracking tag to your website\'s code.'),
      iconName: 'SingleColumnEdit',
    },
    emailTag: {
      title: _TL_('Let me send the tracking tag to someone else'),
      text: _TL_('We\'ll send the tracking tag and instructions to the people you specify, so they can add the tag to your website\'s code.'),
      iconName: 'Mail',
    },
    googleTagManager: {
      title: _TL_('Automatically add the tracking tag through Google Tag Manager'),
      text: _TL_('If you are already using Google Tag Manager for your website, we can automatically set up the tracking tag for you.'),
      iconName: 'SingleColumnEdit',
    },
  }), []);

  const selectedOptions = useMemo(() => map(options, option => ({
    key: option,
    text: '',
    ariaLabel: i18n.getString(optionStrings[option].title),
    onRenderField: (_props, render) => (
      <React.Fragment>
        {render(_props)}
        <div className={`text ${option}-text`}>
          <div className={`title-container ${option}-title-container`}>
            <FontIcon
              iconName={optionStrings[option].iconName}
              className={`title-icon ${option}-title-icon`}
            />
            <div className={`title-text ${option}-title-text `}>{i18n.getString(optionStrings[option].title)}</div>
          </div>
          <div className={`description ${option}-description`}>{i18n.getString(optionStrings[option].text)}</div>
        </div>
      </React.Fragment>
    ),
  })), [i18n, options, optionStrings]);

  const onChangeSelectedOptions = useCallback((a, option) => {
    instrumentation.logTrace(`[EditWebsiteView] Select option: ${option}`);
    if (option.key === options.THIRD_PARTY_TAG) {
      instrumentation.logTrace('[EditWebsiteView] Show platform selector');
    }
    onChangeOptions(option.key);
  }, [instrumentation, options.THIRD_PARTY_TAG, onChangeOptions]);


  return (<ChoiceGroup
    className="edit-website-options"
    selectedKey={selectedOption}
    options={selectedOptions}
    onChange={onChangeSelectedOptions}
    required
    styles={optionSelectorStyle}
  />);
};


OptionsChoiceGroup.propTypes = {
  onChangeOptions: PropTypes.func.isRequired,
  isEmailSupported: PropTypes.bool,
  isGTMEnabled: PropTypes.bool.isRequired,
  selectedOption: PropTypes.string,
};

OptionsChoiceGroup.defaultProps = {
  isEmailSupported: IS_EMAIL_SUPPORTED_ENABLED,
  selectedOption: undefined,
};

